import { useCallback, useEffect, useState } from 'react';
import { applyTheme, getStoredTheme, getSystemTheme, resolveTheme, type Theme } from './index.ts';

export interface UseThemeReturn {
    /** The stored preference — 'auto' | 'light' | 'dark'. */
    theme: Theme;
    /** The resolved display value — always 'light' or 'dark'. */
    resolvedTheme: 'light' | 'dark';
    setTheme: (t: Theme) => void;
}

/**
 * React hook for the app's theme state.
 * Reads the stored theme on mount, exposes a setter, and re-resolves
 * whenever the system preference changes while `theme === 'auto'`.
 */
export function useTheme(): UseThemeReturn {
    const [theme, setThemeState] = useState<Theme>(getStoredTheme);
    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
        resolveTheme(getStoredTheme()),
    );

    const setTheme = useCallback((t: Theme) => {
        applyTheme(t);
        setThemeState(t);
        setResolvedTheme(resolveTheme(t));
    }, []);

    // Re-resolve when system preference changes and we're in 'auto' mode.
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        function handleChange() {
            if (getStoredTheme() === 'auto') {
                const resolved = getSystemTheme();
                document.documentElement.dataset.theme = resolved;
                setResolvedTheme(resolved);
            }
        }
        mq.addEventListener('change', handleChange);
        return () => mq.removeEventListener('change', handleChange);
    }, []);

    return { theme, resolvedTheme, setTheme };
}
